import { isSafeApp } from '@/config/safe/isSafeApp'
import { useWalletType } from '@/domain/hooks/useWalletType'
import { useActionsSettings } from '@/domain/state'
import { useConnectedAddress } from '@/domain/wallet/useConnectedAddress'
import { useEffect, useMemo, useRef, useState } from 'react'
import { TransactionReceipt } from 'viem'
import { useChainId, useConfig } from 'wagmi'
import { getFakePermitAction } from '../flavours/permit/logic/getFakePermitAction'
import { useCreatePermitHandler } from '../flavours/permit/logic/useCreatePermitHandler'
import { getFakePermit2SignAction } from '../flavours/permit2-sign/logic/getFakePermit2SignAction'
import { useCreatePermit2SignHandler } from '../flavours/permit2-sign/logic/useCreatePermit2SignHandler'
import { createPermit2Store } from './permit2Store'
import { createPermitStore } from './permits'
import { Action, ActionContext, ActionHandler, InjectedActionsContext, Objective } from './types'
import { useContractAction } from './useContractAction'
import { useCreateActions } from './useCreateActions'

export interface UseActionHandlersOptions {
  onFinish?: () => void
  enabled: boolean
  context?: InjectedActionsContext
}

export interface UseActionHandlersResult {
  handlers: ActionHandler[]
  settingsDisabled: boolean // @note: after first interaction, we don't enable for settings to change
  actionsInProgress: boolean
}

export function useActionHandlers(
  objectives: Objective[],
  { onFinish, enabled, context: injectedContext }: UseActionHandlersOptions,
): UseActionHandlersResult {
  const actionsSettings = useActionsSettings()
  const walletType = useWalletType()
  const safeActionsSettings = useMemo(() => {
    if (walletType === 'gnosis-safe' || (walletType === undefined && isSafeApp())) {
      return { ...actionsSettings, preferPermits: false }
    }
    return actionsSettings
  }, [actionsSettings, walletType])
  const permitStore = useMemo(() => createPermitStore(), [])
  const permit2Store = useMemo(() => createPermit2Store(), [])
  const txReceipts = useRef<[Action, TransactionReceipt][]>([]).current
  const chainId = useChainId()
  const { account } = useConnectedAddress()
  const wagmiConfig = useConfig()
  const actionContext: ActionContext = {
    ...injectedContext,
    permitStore,
    permit2Store,
    txReceipts,
    wagmiConfig,
    account,
    chainId,
  }
  const actions = useCreateActions({
    objectives,
    actionsSettings: safeActionsSettings,
    actionContext,
  })

  const [currentActionIndex, setCurrentActionIndex] = useState(0)
  const currentAction = actions[currentActionIndex] ?? null

  const handlers: ActionHandler[] = actions.map((action, index) => ({
    action,
    onAction: () => {},
    state: { status: index === currentActionIndex ? 'ready' : index < currentActionIndex ? 'success' : 'disabled' },
  }))

  const handler = useContractAction({
    action: currentAction ?? getFakePermitAction(),
    context: actionContext,
    enabled:
      currentAction !== null && currentAction.type !== 'permit' && currentAction.type !== 'permit2Sign' && enabled,
  })

  const permitHandler = useCreatePermitHandler(
    currentAction?.type === 'permit' ? currentAction : getFakePermitAction(),
    {
      enabled: enabled && currentAction?.type === 'permit',
      permitStore,
    },
  )

  const permit2SignHandler = useCreatePermit2SignHandler(
    currentAction?.type === 'permit2Sign' ? currentAction : getFakePermit2SignAction(),
    {
      enabled: enabled && currentAction?.type === 'permit2Sign',
      permit2Store,
    },
  )

  // Select the appropriate handler based on action type
  const currentActionHandler =
    currentAction === null
      ? null
      : currentAction.type === 'permit'
        ? permitHandler
        : currentAction.type === 'permit2Sign'
          ? permit2SignHandler
          : handler

  useEffect(() => {
    if (currentActionHandler?.state.status === 'success') {
      if (currentActionIndex === actions.length - 1) {
        onFinish?.()
      } else {
        setCurrentActionIndex(currentActionIndex + 1)
      }
    }
  }, [currentActionHandler?.state.status, currentActionIndex, actions.length, onFinish])

  if (currentActionHandler) {
    handlers[currentActionIndex] = currentActionHandler
  }

  const settingsDisabled = currentActionIndex > 0
  const actionsInProgress =
    currentActionIndex > 0 ||
    currentActionHandler?.state.status === 'success' ||
    currentActionHandler?.state.status === 'loading'

  return {
    handlers,
    settingsDisabled,
    actionsInProgress,
  }
}
